/**
 * Fonte única da contagem de "meus pendentes" (tickets needs_action atribuídos a mim).
 * Reusa a mesma queryKey da fase 1 → cache compartilhado com o subtítulo do painel, e as
 * mutations que invalidam 'tickets-needs-action-count' atualizam todos os consumidores.
 * Polling de 60s, **inclusive com a aba em background**: é justamente o caso "estou em
 * outra aba" que o alerta precisa cobrir (spec 2026-08-26 §8). O Chrome estrangula timers
 * de aba oculta para ~1x/min após 5 min, o que já casa com o intervalo — sem custo extra.
 *
 * Troca p/ backend: quando houver endpoint dedicado de contagem, troque fetchMyPendingCount.
 */
import {useQuery} from 'react-query';
import {useAuth} from 'app/modules/auth';
import {serializeTicketFilters, needsMyAction} from '../ticketsHelpers';
import {listTickets} from '../requests/tickets';
import {FIXTURE_TICKETS} from '../ticketsFixtures';

// Sem a base da API (dev local), cai no fixture (mesma regra do TicketsQueriesContext).
const USE_FIXTURE = !process.env.REACT_APP_TICKETS_API_URL;

export const myPendingCountKey = (uid?: string) => [
  'tickets-needs-action-count',
  uid,
];

export const fetchMyPendingCount = (uid: string): Promise<number> =>
  USE_FIXTURE
    ? Promise.resolve(
        FIXTURE_TICKETS.filter((t) => needsMyAction(t, uid)).length
      )
    : listTickets(
        serializeTicketFilters({
          needs_action: true,
          assignee_uid: [uid],
          limit: 1,
        })
      ).then((r) => r.total);

// Retorna number | undefined (undefined = carregando / sem uid). Consumidores fazem `?? 0`.
export const useMyPendingCount = (): number | undefined => {
  const myUid = useAuth().currentUser?.uid;
  const {data} = useQuery({
    queryKey: myPendingCountKey(myUid),
    queryFn: () => fetchMyPendingCount(myUid as string),
    enabled: Boolean(myUid),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
  return data;
};
